import { ACTIVITY_TYPES, type ActivityType } from '../../activities/types'
import { buildActivityPayload } from '../mappers/activities'
import { buildProspectPayload } from '../mappers/prospects'
import { buildWorkflowPayload } from '../mappers/workflow'
import { ActivitiesRepository } from '../repositories/activities.repository'
import { ProspectsRepository } from '../repositories/prospects.repository'
import { WorkflowRepository } from '../repositories/workflow.repository'
import { AdminService } from './admin.service'
import {
  FollowUpService,
  type ScheduleWorkflowFollowUpInput,
} from './followUps.service'
import { unwrap } from './result'

type WorkflowRecord = Record<string, unknown>

export type WorkflowState =
  | 'active'
  | 'waiting'
  | 'completed'
  | 'blocked'
  | 'cancelled'
export type WorkflowActionStatus =
  | 'pending'
  | 'active'
  | 'completed'
  | 'overdue'
  | 'blocked'

export interface WorkflowActivityInput {
  activityType: ActivityType
  description: string
  channel?: string
  result?: string
  receivedResponse?: boolean
  interestLevel?: string
  nextFollowUpAt?: string
  workflowActionType?: string
}

export interface NextAction {
  prospectId: string
  stepCode: string
  actionType: string
  actionLabel: string
  description: string
  dueAt: string | null
  stepNumber: number | null
  totalSteps: number | null
  workflowState: WorkflowState
  actionStatus: WorkflowActionStatus
  isBlocked: boolean
}

export type DailyAction = NextAction & {
  prospectName: string
  responsible: string | null
  interestLevel: string | null
  status: string
  stepName: string
}

export interface WorkflowHistoryItem {
  id: string
  previousStep: string | null
  nextStep: string | null
  changeType: string | null
  reason: string | null
  userName: string | null
  occurredAt: string | null
}

export const WorkflowService = {
  async scheduleFollowUp(input: ScheduleWorkflowFollowUpInput) {
    return FollowUpService.schedule(input)
  },

  async retryFollowUpActivity(
    input: Omit<
      ScheduleWorkflowFollowUpInput,
      'stepCode' | 'recommendedChannel'
    > & { channel: string },
  ) {
    return FollowUpService.retryActivity(input)
  },

  async createActivity(
    prospectId: string,
    input: WorkflowActivityInput,
  ): Promise<void> {
    const user = await AdminService.getCurrentUser()
    if (!user) throw new Error('No fue posible verificar la sesión activa.')

    const payload = buildActivityPayload({
      prospect_id: prospectId,
      user_id: user.id,
      activity_type: input.activityType,
      occurred_at: new Date().toISOString(),
      channel: input.channel,
      description: input.description,
      result: input.result,
      received_response: input.receivedResponse ?? false,
      next_follow_up_at: input.nextFollowUpAt,
    })
    unwrap(await ActivitiesRepository.create(payload))

    if (input.interestLevel) {
      const prospect = unwrap(await ProspectsRepository.getById(prospectId))
      if (!prospect)
        throw new Error(
          'La actividad fue registrada, pero no se pudo actualizar el prospecto.',
        )

      const form = {
        ...prospect,
        interest_level: input.interestLevel,
        estimated_value:
          prospect.estimated_value === null
            ? ''
            : String(prospect.estimated_value),
        currency: prospect.currency ?? 'CRC',
      } as Parameters<typeof buildProspectPayload>[0]

      unwrap(
        await ProspectsRepository.update(
          prospectId,
          buildProspectPayload(
            form,
            String(prospect.assigned_to ?? prospect.created_by ?? user.id),
            'update',
          ),
        ),
      )
    }

    if (input.workflowActionType) await advanceAfterActivity(prospectId)
  },

  async loadNextAction(prospectId: string): Promise<NextAction> {
    const [workflowResult, stepsResult] = await Promise.all([
      WorkflowRepository.getProspectWorkflow(prospectId),
      WorkflowRepository.listSteps(),
    ])
    const workflow = unwrap(workflowResult)
    const steps = unwrap(stepsResult)

    if (!workflow)
      throw new Error('No se pudo determinar la siguiente acción del workflow.')

    const current = steps.find(
      (step) => String(step.id) === String(workflow.current_step_id),
    )
    const state = normalizeState(text(workflow.state, 'active'))
    const blocked = Boolean(workflow.blocked) || Boolean(workflow.is_blocked)
    const dueAt = nullableText(workflow.next_action_due_at)
    const stepCode = text(current?.code, 'pending')

    return {
      prospectId,
      stepCode,
      actionType: state === 'completed' ? 'completed' : stepCode,
      actionLabel: stepCode,
      description: '',
      dueAt,
      stepNumber: numberOrNull(current?.step_order),
      totalSteps: steps.length || null,
      workflowState: state,
      actionStatus:
        state === 'completed'
          ? 'completed'
          : blocked || state === 'blocked'
            ? 'blocked'
            : isOverdue(dueAt)
              ? 'overdue'
              : 'pending',
      isBlocked: blocked,
    }
  },

  async loadDailyActionQueue(): Promise<DailyAction[]> {
    return unwrap(await WorkflowRepository.listQueue()).map((record) => {
      const prospect = object(record.prospects)
      const step = object(record.workflow_steps)
      const state = normalizeState(text(record.state, 'active'))
      const blocked = Boolean(record.blocked) || Boolean(record.is_blocked)
      const dueAt = nullableText(record.next_action_due_at)
      const stepCode = text(step.code, record.current_step_code, 'pending')

      return {
        prospectId: text(record.prospect_id),
        stepCode,
        actionType: state === 'completed' ? 'completed' : stepCode,
        actionLabel: stepCode,
        description: '',
        dueAt,
        stepNumber: numberOrNull(step.step_order),
        totalSteps: null,
        workflowState: state,
        actionStatus:
          state === 'completed'
            ? 'completed'
            : blocked || state === 'blocked'
              ? 'blocked'
              : isOverdue(dueAt)
                ? 'overdue'
                : 'pending',
        isBlocked: blocked,
        prospectName: text(
          prospect.business_name,
          record.business_name,
          'Prospecto',
        ),
        responsible: nullableText(
          prospect.assigned_to_name,
          record.responsible_name,
          record.assigned_to_name,
        ),
        interestLevel: nullableText(
          prospect.interest_level,
          record.interest_level,
        ),
        status: text(prospect.status, record.status),
        stepName: stepCode,
      }
    })
  },

  async history(prospectId: string): Promise<WorkflowHistoryItem[]> {
    return unwrap(await WorkflowRepository.listHistory(prospectId)).map(
      (record) => ({
        id: text(record.id),
        previousStep: nullableText(
          record.from_step_name,
          record.from_step_code,
        ),
        nextStep: nullableText(record.to_step_name, record.to_step_code),
        changeType: nullableText(record.change_type),
        reason: nullableText(record.reason),
        userName: nullableText(record.performed_by),
        occurredAt: nullableText(record.occurred_at),
      }),
    )
  },
}

async function advanceAfterActivity(prospectId: string) {
  const workflow = unwrap(
    await WorkflowRepository.getProspectWorkflow(prospectId),
  )
  if (!workflow) return

  const state = normalizeState(text(workflow.state, 'active'))
  if (
    ['completed', 'cancelled', 'blocked'].includes(state) ||
    Boolean(workflow.blocked) ||
    Boolean(workflow.is_blocked)
  )
    return

  const steps = unwrap(await WorkflowRepository.listSteps())
  const currentIndex = steps.findIndex(
    (step) => String(step.id) === String(workflow.current_step_id),
  )
  const nextStep = currentIndex >= 0 ? steps[currentIndex + 1] : null
  const payload = nextStep
    ? buildWorkflowPayload({
        current_step_id: nextStep.id as string,
        next_action_due_at: null,
        state: 'active',
      })
    : buildWorkflowPayload({ next_action_due_at: null, state: 'completed' })

  unwrap(await WorkflowRepository.advance(prospectId, payload))
}

function object(value: unknown): WorkflowRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as WorkflowRecord)
    : {}
}

function text(...values: unknown[]): string {
  const value = values.find(
    (candidate) => typeof candidate === 'string' && candidate.trim(),
  )
  return typeof value === 'string' ? value.trim() : ''
}

function nullableText(...values: unknown[]) {
  return text(...values) || null
}

function numberOrNull(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function normalizeState(value: string): WorkflowState {
  return ['active', 'waiting', 'completed', 'blocked', 'cancelled'].includes(
    value,
  )
    ? (value as WorkflowState)
    : 'active'
}

function isOverdue(value: string | null) {
  if (!value) return false
  const date = new Date(value)
  return !Number.isNaN(date.getTime()) && date.getTime() < Date.now()
}
